import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import type { Language, SupportedMarketKey } from '@constants/format'
import Autocomplete from '@flows-ui/components/AddressAutocomplete/Autocomplete'
import { ControlledTextField } from '@flows-ui/helperComponents/ControlledTextField'
import { useApiService } from '@flows-ui/services/ApiServiceContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { resolveStepContentElementValue } from '@flows-ui/utils/resolveStepContentElementValue'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { AddressAutoComplete as AddressAutocompleteProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import { getLocationStepContent } from '@flows/index'
import Alert from '@ui-kit/components/Alert'
import { useAddressAutocomplete } from '@ui-kit/components/formComponents/Autocomplete/hooks'
import TextField from '@ui-kit/components/formComponents/TextField'
import Stack from '@ui-kit/components/layoutComponents/Stack'
import { body2 } from '@ui-kit/core/typography'

type AddressDetail = {
  street: string
  houseNumber: string
  city: string
  country: string
  postalCode: string
  url: string
  lat: number
  lng: number
  image: string
}

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends AddressAutocompleteProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
  market: SupportedMarketKey
  language: Language
  flowName: string
}

const autocompleteStyles = tv({
  slots: {
    buttonContainer: 'inline cursor-pointer text-deep-forest underline',
    imageContainer: 'w-full grayscale filter',
  },
})

export const AddressAutocomplete = <TProps extends Props<any, any>>(props: TProps) => {
  const styles = autocompleteStyles()
  const { t } = useTranslation()
  const { service, apiBases } = useApiService()

  const { status, loadPredictions, loadAddressDetail } = useAddressAutocomplete({
    get: async (endpoint: string) => {
      const response = await service?.client.get(`${apiBases?.personalizationService}/${endpoint}`)
      if (!response) {
        throw new Error(`Failed to fetch data from endpoint: ${endpoint}`)
      }
      return response
    },
  })
  const [addressDetail, setAddressDetail] = React.useState<AddressDetail | null>(null)
  const [shouldEnterAddressManually, setShouldEnterAddressManually] = React.useState(false)
  const { setValue, watch, setFocus, formState } = useFormContext()

  const formValues = watch()
  const hasAddressData = formValues.street || formValues.houseNumber || formValues.town

  const updateAddressDetail = async (
    placeId: string,
    dimensions?: { width: string; height: string }
  ) => {
    const _addressDetail = await loadAddressDetail(
      placeId,
      dimensions
        ? {
            width: dimensions.width,
            height: dimensions.height,
          }
        : undefined
    )

    setAddressDetail(_addressDetail)
    setValue('street', _addressDetail?.street, { shouldValidate: true })
    setValue('houseNumber', _addressDetail?.houseNumber, { shouldValidate: true })
    setValue('town', _addressDetail?.city, { shouldValidate: true })

    setValue('zip_code', _addressDetail?.postalCode, { shouldValidate: true })

    return {
      street: _addressDetail?.street,
      houseNumber: _addressDetail?.houseNumber,
      zip_code: _addressDetail?.postalCode,
      town: _addressDetail?.city,
    }
  }

  const locationStepContent = getLocationStepContent(props.market).map((stepContent) =>
    stepContent.type === 'input' && stepContent.name === 'zip_code'
      ? {
          ...stepContent,
        }
      : stepContent
  )

  const elementPatterns = locationStepContent
    .filter(
      (stepContent): stepContent is Extract<typeof stepContent, { name: string }> =>
        'name' in stepContent
    )
    .map((stepContent) => stepContent.name)

  // @ts-expect-error we want to narrow down the type here and TypeScript doesn't like this,
  // but `includes will work properly
  const hasAddressError = Object.keys(formState.errors).some((e) => elementPatterns.includes(e))
  const inputError = hasAddressError ? t('addressAutocomplete.empty') : undefined

  const getAddresslines = (val: string, name: string) => {
    const addressLines = locationStepContent
      .filter(
        (content): content is Extract<typeof content, { name: string }> =>
          content.type === 'input' && content.name !== name && content.name !== 'country'
      )
      .map((content) => formValues[content.name])
      .concat(val)
      .filter(Boolean)

    return addressLines
  }

  const textProcessor = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses: {}, // returning empty object for now since we don't have any services responses
  })

  const { control } = useFormContext()

  return (
    <Stack>
      {shouldEnterAddressManually ? null : (
        <Autocomplete
          errorMessage={inputError}
          name={props.name}
          label={t('addressAutocomplete.searchYourAddress')}
          styleType="outlined"
          onValueChange={async (val) => {
            if (val) {
              const updatedAddressDetail = await updateAddressDetail(val.toString(), {
                width:
                  document.getElementById('flow_step_container')?.clientWidth.toString() ?? '400',
                height: '150',
              })

              if (updatedAddressDetail) {
                const firstEmptyField = locationStepContent.find(
                  (item): item is Extract<typeof item, { name: string }> =>
                    item.type === 'input' &&
                    item.name !== 'country' &&
                    !updatedAddressDetail[item.name]
                )?.name

                if (firstEmptyField) {
                  setTimeout(() => setFocus(firstEmptyField), 0)
                }
              }
            }
          }}
          onChangeFilterInputValue={async (filter) => {
            await loadPredictions(filter, props.market)
          }}
          status={status}
        />
      )}
      {status.name === 'error' ? <Alert message={status.error_message} /> : null}
      {hasAddressData || shouldEnterAddressManually ? (
        <Stack space="xsmall">
          {addressDetail?.image && !shouldEnterAddressManually ? (
            <View className="relative flex flex-col pb-2.5">
              <img
                alt="static map of selected location"
                src={`data:image/jpeg;base64, ${addressDetail.image}`}
                className={styles.imageContainer()}
              />
            </View>
          ) : null}
          {locationStepContent
            .filter(
              (
                stepContent
              ): stepContent is Extract<typeof stepContent, { name: string; label: any }> =>
                'name' in stepContent && 'label' in stepContent
            )
            .map((stepContent) => {
              return (
                <View className="relative flex flex-col" key={stepContent.name}>
                  {'value' in stepContent && stepContent.value ? (
                    <TextField
                      id={`address-autocomplete-${stepContent.name}`}
                      label={textProcessor(stepContent.label)}
                      name={stepContent.name}
                      value={resolveStepContentElementValue(stepContent.value, textProcessor)}
                      styleType="outlined"
                      disabled
                    />
                  ) : (
                    <ControlledTextField
                      id={`address-autocomplete-${stepContent.name}`}
                      control={control}
                      label={textProcessor(stepContent.label)}
                      name={stepContent.name}
                      styleType="outlined"
                      disabled={'disabled' in stepContent ? stepContent.disabled : false}
                      onValueChange={async (val) => {
                        if (val === undefined || shouldEnterAddressManually) {
                          return
                        }
                        const addressLines = getAddresslines(val.toString(), stepContent.name)

                        const result = await service?.client
                          .post(
                            `${apiBases?.personalizationService}/v1/address_autocomplete/verify`,
                            {
                              address: {
                                regionCode: props.market.toUpperCase(),
                                addressLines,
                              },
                            }
                          )
                          .catch(() => {
                            return
                          })

                        if (result?.data?.result?.geocode?.placeId) {
                          const _addressDetail = await loadAddressDetail(
                            result.data.result.geocode.placeId
                          )

                          setAddressDetail(_addressDetail)
                          if (stepContent.name !== 'street') {
                            setValue('street', _addressDetail?.street, { shouldValidate: true })
                          }
                          if (stepContent.name !== 'houseNumber') {
                            setValue('houseNumber', _addressDetail?.houseNumber, {
                              shouldValidate: true,
                            })
                          }
                          if (stepContent.name !== 'zip_code') {
                            setValue('zip_code', _addressDetail?.postalCode, {
                              shouldValidate: true,
                            })
                          }
                          if (stepContent.name !== 'town') {
                            setValue('town', _addressDetail?.city, { shouldValidate: true })
                          }
                        }
                      }}
                    />
                  )}
                </View>
              )
            })}
        </Stack>
      ) : null}
      {hasAddressData && !shouldEnterAddressManually ? null : (
        <View className="flex flex-row items-center">
          <Text className={body2()}>{t('addressAutocomplete.or')}&nbsp;</Text>
          <Pressable
            className={styles.buttonContainer()}
            id={'manual_address'}
            onPress={() => {
              setShouldEnterAddressManually((prevState) => !prevState)
            }}
          >
            <Text className={body2()}>
              {shouldEnterAddressManually
                ? t('addressAutocomplete.enter.search')
                : t('addressAutocomplete.enter.manual')}
            </Text>
          </Pressable>
        </View>
      )}
    </Stack>
  )
}
